import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { Router } from '@angular/router';

@Injectable({
	providedIn: 'root',
})
export class PushNotificationService {
	public permission: Permission;

	constructor(private router: Router) {
		this.permission = this.isSupported() ? 'default' : 'denied';
	}

	public isSupported(): boolean {
		return 'Notification' in window;
	}

	private isMobile(): boolean {
		return /android|iphone|ipad|ipod|webos|blackberry|iemobile|opera mini/i.test(
			navigator.userAgent
		);
	}

	requestPermission(): void {
		if ('Notification' in window) {
			Notification.requestPermission()
				.then((status) => {
					this.permission = status;
				})
				.catch(() => {
					this.permission = 'denied';
				});
		}
	}

	create(title: string, options?: PushNotification): any {
		const vm = this;
		return new Observable(function (obs) {
			let _notify = new Notification(title, options);
			_notify.onshow = function (e) {
				return obs.next({
					notification: _notify,
					event: e,
				});
			};
			_notify.onclick = function (e) {
				if (options?.url) {
					vm.router.navigateByUrl(options.url);
				}
				return obs.next({
					notification: _notify,
					event: e,
				});
			};
			_notify.onerror = function (e) {
				return obs.error({
					notification: _notify,
					event: e,
				});
			};
			_notify.onclose = function () {
				return obs.complete();
			};
		});
	}

	private async showViaServiceWorker(title: string, options?: PushNotification): Promise<void> {
		if (!navigator.serviceWorker.controller) {
			console.warn('No active service worker, falling back to Notification API');
			this.create(title, options).subscribe();
			return;
		}
		const registration = await navigator.serviceWorker.ready;
		await registration.showNotification(title, {
			body: options?.body || '',
			icon: options?.icon || 'assets/images/icon.png',
			data: { url: options?.url },
		});
	}

	generateNotification(source: Array<any>): void {
		try {
			source.forEach((item) => {
				const options: PushNotification = {
					body: item.alertContent,
					icon: 'assets/images/icon.png',
					url: item?.url,
				};

				if (this.isMobile() && 'serviceWorker' in navigator) {
					this.showViaServiceWorker(item.title, options).catch(() => {
						this.create(item.title, options).subscribe();
					});
				} else {
					this.create(item.title, options).subscribe();
				}
			});
		} catch (error) {
			console.warn('failed to generateNotification');
		}
	}
}

export declare type Permission = 'denied' | 'granted' | 'default';

export interface PushNotification {
	body?: string;
	icon?: string;
	tag?: string;
	data?: any;
	renotify?: boolean;
	silent?: boolean;
	sound?: string;
	noscreen?: boolean;
	sticky?: boolean;
	dir?: 'auto' | 'ltr' | 'rtl';
	lang?: string;
	vibrate?: number[];
	url?: string;
}
